import gsap from "gsap";

// Tune the feel of the animation here.
const CFG = {
  duration: 0.8, // seconds per animation step
  hold: 0.6, // seconds to pause at each frame
  ease: "power2.inOut" as gsap.EaseString
};

// Tracks the current x/y position of each letter so we can correctly
// interpolate from the right starting point in each step.
const pos: Record<string, { x: number; y: number }> = {
  D1: { x: 0, y: 0 },
  U: { x: 0, y: 0 },
  N: { x: 0, y: 0 },
  D2: { x: 0, y: 0 },
  R: { x: 0, y: 0 }
};

// Animate a mask letter group using SVG transform attribute (not CSS transform).
// CSS transforms are ignored inside <mask>, so we use a GSAP proxy object and
// write the SVG transform attribute on every frame via onUpdate.
function go(
  tl: gsap.core.Timeline,
  key: string,
  el: Element,
  props: { x?: number; y?: number },
  simultaneous = false
) {
  const from = { ...pos[key] };
  const to = { x: props.x ?? from.x, y: props.y ?? from.y };
  const proxy = { x: from.x, y: from.y };

  tl.to(
    proxy,
    {
      x: to.x,
      y: to.y,
      duration: CFG.duration,
      ease: CFG.ease,
      onUpdate() {
        el.setAttribute("transform", `translate(${proxy.x}, ${proxy.y})`);
      }
    },
    simultaneous ? "<" : undefined
  );

  pos[key] = to;
}

function pause(tl: gsap.core.Timeline) {
  if (CFG.hold > 0) tl.to({}, { duration: CFG.hold });
}

function buildTimeline(els: Record<string, Element>): gsap.core.Timeline {
  Object.keys(pos).forEach((k) => {
    pos[k] = { x: 0, y: 0 };
    els[k].setAttribute("transform", "translate(0, 0)");
  });

  const tl = gsap.timeline();

  // F1 — home position, hold
  pause(tl);

  // F1→F2: D1+U right, N+D2+R left (simultaneous)
  go(tl, "D1", els.D1, { x: +241 });
  go(tl, "U", els.U, { x: +240 }, true);
  go(tl, "N", els.N, { x: -60 }, true);
  go(tl, "D2", els.D2, { x: -60 }, true);
  go(tl, "R", els.R, { x: -126 }, true);
  pause(tl);

  // F2→F3: R slides further left
  go(tl, "R", els.R, { x: -307 });
  pause(tl);

  // F3→F4: all back to origin, R moves right (simultaneous)
  go(tl, "D1", els.D1, { x: 0 });
  go(tl, "U", els.U, { x: 0 }, true);
  go(tl, "N", els.N, { x: 0 }, true);
  go(tl, "D2", els.D2, { x: 0 }, true);
  go(tl, "R", els.R, { x: +140 }, true);
  pause(tl);

  // F4→F5: D2 slides down
  go(tl, "D2", els.D2, { y: +176 });
  pause(tl);

  // F5→F6: N slides right
  go(tl, "N", els.N, { x: +301 });
  pause(tl);

  // F6→F7: N, D2, R slide up (simultaneous) — final position
  go(tl, "N", els.N, { y: -175 });
  go(tl, "D2", els.D2, { y: 0 }, true);
  go(tl, "R", els.R, { y: -175 }, true);

  return tl;
}

function init() {
  const guard = document.getElementById("mask-D1");
  if (!guard) return;

  const els: Record<string, Element> = {
    D1: document.getElementById("mask-D1")!,
    U: document.getElementById("mask-U")!,
    N: document.getElementById("mask-N")!,
    D2: document.getElementById("mask-D2")!,
    R: document.getElementById("mask-R")!
  };

  buildTimeline(els);
}

export default function heroAnimation() {
  if (document.readyState !== "loading") {
    init();
  } else {
    document.addEventListener("DOMContentLoaded", init);
  }
}
